import { FormStatusEnum } from "@/components/molecules/FormWrapper/FormWrapper.types";
import { useEffect, useState } from "react";
import { Instrument, OrderEntity, OrderStatusEnum, OrderTypeEnum } from "../../modules/orders/domain/entities/OrdersEntity";
import { useQueryClient } from "@tanstack/react-query";
import { useTitle } from "@/core/context/TitleContext";
import { useOrdersData } from "../../modules/orders/application/hooks/useOrdersData";
import { useCreateOrder, useDeleteOrder, useUpdateOrder } from "../../modules/orders/application/hooks/useOrdersQueries";
import { Loading } from "@/components/atoms/Loading/Loading";
import { FormHeader } from "@/components/molecules/FormHeader/FormHeader";
import { DataTable } from "@/components/molecules/DataTable/DataTable";
import { columns, OrderFormData, orderKeys } from "../../modules/orders/infrastructure/OrdersConstants";
import { OrdersForm } from "../../modules/orders/presentation/OrdersForm";
import { usePromptDialogs } from "@/core/context/DialogContext";

// Componente funcional
export const OrdersPage = () => {
  const [formStatus, setFormStatus] = useState<FormStatusEnum>(FormStatusEnum.List);
  const [instrumentSelected, setInstrumentSelected] = useState<Instrument>();
  const [orderSelected, setOrderSelected] = useState<OrderEntity>();
  const [, setFormKey] = useState(0);
  const { setTitle } = useTitle();
  const promptDialog = usePromptDialogs();

  const queryClient = useQueryClient();
  const {
    orders,
    rates,
    portfolios,
    portfolioOptions,
    instrumentOptions,
    instruments,
    isLoading,
    ordersRefetch,
  } = useOrdersData();

  // effects
  useEffect(() => {
    setTitle("Ordenes");
  }, []);

  useEffect(() => {
    orderSelected?.instrument && setInstrumentSelected(orderSelected.instrument);
  }, [orderSelected]);

  // hooks
  const createOrderMutation = useCreateOrder();
  const updateOrderMutation = useUpdateOrder();
  const deleteOrderMutation = useDeleteOrder();

  const onChange = (id: string) => {
    setInstrumentSelected(instruments.find(instrument => instrument.id === id));
  }

  const onSubmit = async (formData: OrderFormData) => {
    let success = false;
    if (formStatus === FormStatusEnum.Add) {
      const { id, ...payload } = formData;
      const resp = await createOrderMutation.mutateAsync({
        ...payload,
        orderType: payload.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA
      });
      success = resp.success;
    } else {
      const resp = await updateOrderMutation.mutateAsync({
        id: formData.id ?? "0",
        ...formData,
        orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA
      });
      success = resp.success;
    }
    if (success) {
      await queryClient.invalidateQueries({ queryKey: orderKeys.all });
      handleChangeFormStatus(FormStatusEnum.List);
    }
  };

  const onDelete = async (id: string) => {
    await deleteOrderMutation.mutateAsync(id);
    await queryClient.invalidateQueries({ queryKey: orderKeys.all });
    handleChangeFormStatus(FormStatusEnum.List);
  };

  const onContinue = async (formData: OrderFormData, newStatus: OrderStatusEnum) => {
    if (orderSelected) {
      if (newStatus === OrderStatusEnum.COMPLEMENTACION) {
        const confirmNumber = await promptDialog({
          title: "Número de operación",
          description: "Ingresa el número de operación para complementar el registro.",
          placeholder: "Número de operación",
          variant: "warning",
          defaultValue: orderSelected.confirmNumber
        });
        if (confirmNumber) {
          await updateOrderMutation.mutateAsync({
            id: formData.id ?? "0",
            ...formData,
            status: newStatus,
            orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA,
            confirmNumber
          });
          await queryClient.invalidateQueries({ queryKey: orderKeys.all });
          setOrderSelected(prev => ({
            ...(prev as OrderEntity),
            status: OrderStatusEnum.COMPLEMENTACION,
            confirmNumber
          }));
        }
      } else {
        await updateOrderMutation.mutateAsync({
          id: formData.id ?? "0",
          ...formData,
          status: newStatus,
          orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA
        });
        await queryClient.invalidateQueries({ queryKey: orderKeys.all });
        await setOrderSelected({ ...orderSelected, status: newStatus });
      }

    }
  };

  const handleChangeFormStatus = (statusOrUpdater: FormStatusEnum | ((prevState: FormStatusEnum) => FormStatusEnum)) => {
    const nextStatus = typeof statusOrUpdater === 'function'
      ? (statusOrUpdater as (prev: FormStatusEnum) => FormStatusEnum)(formStatus)
      : statusOrUpdater;

    if (nextStatus === FormStatusEnum.List) {
      setOrderSelected(undefined);
      setInstrumentSelected(undefined);
      setFormKey(prev => prev + 1);
    }

    setFormStatus(nextStatus);
  };

  if (
    isLoading ||
    createOrderMutation.isPending ||
    updateOrderMutation.isPending ||
    deleteOrderMutation.isPending
  ) return <Loading />;

  return (
    <>
      <FormHeader
        title="Renta fija"
        changeFormStatus={handleChangeFormStatus}
        formStatus={formStatus}
        selectItem={setOrderSelected}
        refetch={ordersRefetch}
        canAdd
      />

      {formStatus === FormStatusEnum.List ? (
        <DataTable
          data={orders}
          columns={columns}
          tableHeadClass="bg-primary-500 text-white"
          pageSize={10}
          changeFormStatus={setFormStatus}
          selectItem={setOrderSelected}
          defaultSortKey="createdAt"
          defaultSortOrder="desc"
        />
      ) : (
        <OrdersForm
          onSubmit={onSubmit}
          onDelete={onDelete}
          onContinue={onContinue}
          rates={rates}
          portfolios={portfolios}
          portfolioOptions={portfolioOptions}
          instrumentOptions={instrumentOptions}
          instrumentSelected={instrumentSelected}
          setInstrumentSelected={onChange}
          formStatus={formStatus}
          orderSelected={orderSelected}
        />
      )}
    </>
  );
};
